import type { BusinessClass } from '../types';

export const MAX_BUSINESS_CLASSES = 3;

const MIN_NAME_LENGTH = 2;

export function createBusinessClassId(): string {
  return `cls_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export function createEmptyBusinessClass(): BusinessClass {
  return {
    id: createBusinessClassId(),
    name: '',
    days: '',
    from: '',
    to: '',
    price: '',
  };
}

export function normalizeBusinessClass(cls: BusinessClass): BusinessClass {
  return {
    ...cls,
    name: cls.name?.trim() ?? '',
    days: cls.days?.trim() ?? '',
    from: cls.from?.trim() ?? '',
    to: cls.to?.trim() ?? '',
    price: cls.price?.trim() ?? '',
  };
}

/** At least one class with a valid name. */
export function isBusinessClassComplete(cls: BusinessClass): boolean {
  return cls.name.trim().length >= MIN_NAME_LENGTH;
}

export function hasValidBusinessClasses(classes: BusinessClass[]): boolean {
  return classes.some(isBusinessClassComplete);
}

export function formatBusinessClassLabel(cls: BusinessClass): string {
  const name = cls.name.trim() || 'Clase';
  const days = cls.days.trim();
  const from = cls.from.trim();
  const to = cls.to.trim();

  const parts = [name];
  if (days) parts.push(days);
  if (from && to) parts.push(`${from} – ${to}`);
  else if (from) parts.push(from);

  return parts.join(' · ');
}

export function formatBusinessClassPrice(price: string): string | null {
  const trimmed = price.trim();
  if (!trimmed) return null;
  return trimmed.endsWith('€') ? trimmed : `${trimmed}€`;
}

export function formatBusinessClassSchedule(cls: BusinessClass): string | null {
  const days = cls.days.trim();
  const from = cls.from.trim();
  const to = cls.to.trim();

  if (days && from && to) return `${days} · ${from} – ${to}`;
  if (days) return days;
  if (from && to) return `${from} – ${to}`;
  if (from) return from;
  return null;
}

/** Migrate legacy drafts that only had `days` as schedule text. */
export function normalizeStoredClasses(classes: BusinessClass[] | undefined): BusinessClass[] {
  return (classes ?? []).map((cls) => {
    const normalized = normalizeBusinessClass(cls);
    if (!normalized.name && normalized.days) {
      return { ...normalized, name: normalized.days, days: '' };
    }
    return normalized;
  });
}
